// Portal do Cliente — Administração (2026-07-22).
// Página interna (equipe) pra gerenciar QUEM do cliente acessa o portal de
// monitoramento e a QUÊ. Escreve em monitoramento.portal_usuarios via as rotas
// /monitoramento-admin/* (gate staff monitoramento.portal.gerenciar).
//
// O LOGIN (usuário/senha) é criado quando o portal for publicado (pool Cognito de
// clientes). Por ora esta tela define o acesso; o login entra depois.
import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Table, Tag, Button, Modal, Input, Select, Switch, Space, Typography,
  Popconfirm, Alert, Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { usePerms } from '../hooks/usePerms';

const { Text } = Typography;

type Cliente = { id: number; nome: string; razao_social: string; cnpj: string | null };
type Contrato = { id: string; numero: string | null; titulo: string | null };
type PortalUser = {
  email: string; nome: string | null; cliente_id: number; cliente_nome: string | null;
  papel: 'gestor' | 'comum'; ativo: boolean; contratos: string[];
  criado_em?: string | null;
  primeiro_acesso_em?: string | null;   // NULL = convidado que nunca entrou
  ultimo_acesso_em?: string | null;     // gravado no máx. 1x/min pelo backend
  dias_ativos_30d?: number;
  minutos_ativos_30d?: number;
};

// O portal faz polling de 60s ENQUANTO a aba está visível (aba em segundo
// plano fica muda). Então atividade nos últimos 3 min = o portal está aberto
// na frente da pessoa agora. Janela menor piscaria; maior mentiria.
const ONLINE_JANELA_MS = 3 * 60 * 1000;

// "há 5 min", "há 3 h", "ontem", "há 12 dias" — placar de adoção pede leitura
// imediata; a data exata fica no tooltip.
function tempoRelativo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 60) return `há ${d} dias`;
  return `há ${Math.floor(d / 30)} meses`;
}
const fmtDataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function unwrap(d: any) {
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') {
    try { return JSON.parse(d.body); } catch { return d; }
  }
  return d;
}
async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  const d = unwrap(await r.json().catch(() => ({})));
  if (!r.ok || d?.erro) throw new Error(d?.erro || `Erro ${r.status}`);
  return d;
}

export default function PortalClientesAdmin() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeGerenciar = pode('monitoramento.portal.gerenciar') || pode('admin.permissoes.editar');

  const [usuarios, setUsuarios] = useState<PortalUser[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);

  // Filtros. `situacao` é o recorte de adoção — "quem nunca entrou" é a lista
  // de cobrança, "online" é quem está usando agora.
  const [fCliente, setFCliente] = useState<number | null>(null);
  const [fSituacao, setFSituacao] = useState<'todos' | 'nunca' | 'online' | 'inativo'>('todos');
  const [fBusca, setFBusca] = useState('');
  const [agrupar, setAgrupar] = useState(true);

  // modal
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState<PortalUser | null>(null);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [papel, setPapel] = useState<'gestor' | 'comum'>('comum');
  const [ativo, setAtivo] = useState(true);
  const [contratosSel, setContratosSel] = useState<string[]>([]);
  const [contratosOpts, setContratosOpts] = useState<Contrato[]>([]);
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const [us, cl] = await Promise.all([
        api('/monitoramento-admin/usuarios'),
        api('/monitoramento-admin/clientes'),
      ]);
      setUsuarios(Array.isArray(us) ? us : []);
      setClientes(Array.isArray(cl) ? cl : []);
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e?.message || e));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  // O indicador "online" envelhece parado — sem isto, quem abrisse a tela veria
  // um retrato do momento do carregamento e acreditaria nele meia hora depois.
  // Recarrega só a lista, a cada 60s, e apenas com a aba visível (o mesmo
  // cuidado que o próprio portal do cliente tem com o polling dele).
  useEffect(() => {
    const t = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const us = await api('/monitoramento-admin/usuarios');
        setUsuarios(Array.isArray(us) ? us : []);
      } catch { /* falha de rede transitória: mantém o que está na tela */ }
    }, 60000);
    return () => window.clearInterval(t);
  }, []);

  async function carregarContratos(cid: number | null) {
    if (!cid) { setContratosOpts([]); return; }
    try {
      const cs = await api(`/monitoramento-admin/contratos?cliente_id=${cid}`);
      setContratosOpts(Array.isArray(cs) ? cs : []);
    } catch { setContratosOpts([]); }
  }

  function abrirNovo() {
    setEditando(null);
    setEmail(''); setNome(''); setClienteId(null); setPapel('comum'); setAtivo(true);
    setContratosSel([]); setContratosOpts([]);
    setAberto(true);
  }
  function abrirEditor(u: PortalUser) {
    setEditando(u);
    setEmail(u.email); setNome(u.nome || ''); setClienteId(u.cliente_id);
    setPapel(u.papel); setAtivo(u.ativo); setContratosSel(u.contratos || []);
    carregarContratos(u.cliente_id);
    setAberto(true);
  }

  async function salvar() {
    if (!email.trim()) { message.warning('Informe o e-mail'); return; }
    if (!clienteId) { message.warning('Selecione o cliente'); return; }
    setSalvando(true);
    try {
      await api('/monitoramento-admin/usuarios', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(), nome: nome.trim(), cliente_id: clienteId, papel, ativo,
          contratos: papel === 'gestor' ? [] : contratosSel,
        }),
      });
      message.success('Usuário salvo');
      setAberto(false);
      await carregar();
    } catch (e: any) {
      message.error('Erro ao salvar: ' + (e?.message || e));
    } finally { setSalvando(false); }
  }

  async function remover(u: PortalUser) {
    try {
      await api(`/monitoramento-admin/usuarios/${encodeURIComponent(u.email)}`, { method: 'DELETE' });
      message.success('Usuário removido');
      await carregar();
    } catch (e: any) {
      message.error('Erro ao remover: ' + (e?.message || e));
    }
  }

  async function provisionar(u: PortalUser) {
    try {
      const d = await api('/monitoramento-admin/usuarios/provisionar', {
        method: 'POST', body: JSON.stringify({ email: u.email }),
      });
      modal.success({
        title: d.criado ? 'Acesso criado' : 'Senha redefinida',
        width: 460,
        content: (
          <div>
            <p>Login: <b>{u.email}</b></p>
            <p>Senha temporária: <b style={{ fontSize: 16 }}>{d.senha_temp}</b></p>
            <p style={{ fontSize: 12, color: '#888' }}>
              Repasse ao cliente. Ele define a senha definitiva no primeiro acesso.
            </p>
          </div>
        ),
      });
    } catch (e: any) {
      message.error('Erro ao criar acesso: ' + (e?.message || e));
    }
  }

  const numeroPorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contratosOpts) m.set(c.id, c.numero || c.id.slice(0, 8));
    return m;
  }, [contratosOpts]);

  // Recorte + agrupamento. O "online" muda com o tempo, então o filtro é
  // calculado a cada render (a lista se atualiza a cada 60s).
  const filtrados = useMemo(() => {
    const q = fBusca.trim().toLowerCase();
    const agora = Date.now();
    return usuarios.filter(u => {
      if (fCliente && u.cliente_id !== fCliente) return false;
      if (q && !(`${u.email} ${u.nome || ''} ${u.cliente_nome || ''}`.toLowerCase().includes(q))) return false;
      const online = !!u.ultimo_acesso_em && agora - new Date(u.ultimo_acesso_em).getTime() < ONLINE_JANELA_MS;
      if (fSituacao === 'nunca' && u.ultimo_acesso_em) return false;
      if (fSituacao === 'online' && !online) return false;
      if (fSituacao === 'inativo' && u.ativo) return false;
      return true;
    });
  }, [usuarios, fCliente, fSituacao, fBusca]);

  const grupos = useMemo(() => {
    const m = new Map<number, { cliente_id: number; nome: string; itens: PortalUser[] }>();
    for (const u of filtrados) {
      const g = m.get(u.cliente_id) || { cliente_id: u.cliente_id, nome: u.cliente_nome || '(sem cliente)', itens: [] };
      g.itens.push(u); m.set(u.cliente_id, g);
    }
    return Array.from(m.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [filtrados]);

  const contagem = useMemo(() => {
    const agora = Date.now();
    let nunca = 0, online = 0, usou30 = 0;
    for (const u of usuarios) {
      if (!u.ultimo_acesso_em) nunca++;
      else if (agora - new Date(u.ultimo_acesso_em).getTime() < ONLINE_JANELA_MS) online++;
      if ((u.dias_ativos_30d || 0) > 0) usou30++;
    }
    return { total: usuarios.length, nunca, online, usou30 };
  }, [usuarios]);

  // Colunas: o cliente saiu da linha (vira cabeçalho de grupo — repetido em
  // toda linha, um nome como "CRVR - RIOGRANDENSE VALORIZACAO DE RESIDUO S.A."
  // comia três linhas de altura em cada usuário). E-mail e nome dividem uma
  // célula: são a mesma coisa — a pessoa. Larguras somadas cabem em 1100 px.
  const columns = [
    { title: 'Usuário', key: 'usuario', ellipsis: true,
      render: (_: any, u: PortalUser) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={u.nome || undefined}>
            {u.nome || <Text type="secondary">(sem nome)</Text>}
          </div>
          <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={u.email}>{u.email}</div>
        </div>
      ) },
    ...(agrupar ? [] : [{ title: 'Cliente', dataIndex: 'cliente_nome', key: 'cliente', ellipsis: true, width: 220,
      render: (v: string) => <span title={v}>{v || '—'}</span> }]),
    { title: 'Papel · acesso', key: 'papel', width: 175,
      render: (_: any, u: PortalUser) => (
        <Space size={4} wrap>
          <Tag color={u.papel === 'gestor' ? 'gold' : 'default'} style={{ marginInlineEnd: 0 }}>{u.papel}</Tag>
          {u.papel === 'gestor'
            ? <Text type="secondary" style={{ fontSize: 12 }}>todos do cliente</Text>
            : <Text type="secondary" style={{ fontSize: 12 }}>{u.contratos?.length || 0} contrato(s)</Text>}
        </Space>
      ) },
    { title: 'Ativo', dataIndex: 'ativo', key: 'ativo', width: 80, align: 'center' as const,
      render: (a: boolean) => a
        ? <Tag color="green" style={{ marginInlineEnd: 0 }}>sim</Tag>
        : <Tag color="red" style={{ marginInlineEnd: 0 }}>não</Tag> },
    { title: 'Último acesso', key: 'acesso_quando', width: 140,
      render: (_: any, u: PortalUser) => {
        if (!u.ultimo_acesso_em) {
          // Convite sem resposta é o dado mais acionável da coluna: é quem a
          // equipe precisa cobrar para o portal ter adoção.
          return (
            <Tooltip title={u.criado_em
              ? `Cadastrado em ${fmtDataHora(u.criado_em)} e nunca acessou`
              : 'Nunca acessou o portal'}>
              <Tag color="orange">nunca entrou</Tag>
            </Tooltip>
          );
        }
        const online = Date.now() - new Date(u.ultimo_acesso_em).getTime() < ONLINE_JANELA_MS;
        return (
          <Tooltip title={
            `Último acesso: ${fmtDataHora(u.ultimo_acesso_em)}`
            + (u.primeiro_acesso_em ? ` · Primeiro: ${fmtDataHora(u.primeiro_acesso_em)}` : '')}>
            <Space size={6}>
              {online && <span style={{ width: 8, height: 8, borderRadius: '50%',
                background: '#52c41a', display: 'inline-block',
                boxShadow: '0 0 0 3px rgba(82,196,26,.18)' }} />}
              <Text style={{ fontSize: 12 }} type={online ? undefined : 'secondary'}>
                {online ? 'online agora' : tempoRelativo(u.ultimo_acesso_em)}
              </Text>
            </Space>
          </Tooltip>
        );
      } },
    { title: 'Uso (30d)', key: 'uso30', width: 110,
      render: (_: any, u: PortalUser) => {
        if (!u.ultimo_acesso_em) return <Text type="secondary">—</Text>;
        const dias = u.dias_ativos_30d || 0;
        const min = u.minutos_ativos_30d || 0;
        if (!dias) return <Text type="secondary" style={{ fontSize: 12 }}>sem uso</Text>;
        return (
          <Tooltip title={`Entrou em ${dias} dia(s) nos últimos 30, somando ~${min} min com o portal aberto`}>
            <Text style={{ fontSize: 12 }}>
              {dias} dia{dias > 1 ? 's' : ''} · ~{min < 60 ? `${min} min` : `${Math.round(min / 60)} h`}
            </Text>
          </Tooltip>
        );
      } },
    { title: '', key: 'acao', width: 118, align: 'right' as const,
      render: (_: any, u: PortalUser) => (
        <Space size={4}>
          <Tooltip title={u.ultimo_acesso_em
            ? 'Redefinir o acesso (gera senha temporária nova)'
            : 'Criar o acesso e mostrar a senha temporária'}>
            <Button size="small" icon={<KeyOutlined />}
              type={u.ultimo_acesso_em ? 'default' : 'primary'} ghost={!u.ultimo_acesso_em}
              disabled={!podeGerenciar} onClick={() => provisionar(u)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button size="small" icon={<EditOutlined />} disabled={!podeGerenciar} onClick={() => abrirEditor(u)} />
          </Tooltip>
          <Popconfirm title="Remover este usuário do portal?" okText="Remover" cancelText="Cancelar"
            onConfirm={() => remover(u)} disabled={!podeGerenciar}>
            <Tooltip title="Remover">
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!podeGerenciar} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ) },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Portal do Cliente — Usuários</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} disabled={!podeGerenciar} onClick={abrirNovo}>
          Novo usuário
        </Button>
      </Space>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Isto define quem do cliente acessa o monitoramento e a quais contratos."
        description="O gestor vê todas as estações do cliente; o usuário comum vê só os contratos marcados. A chave (🔑) cria ou redefine o login e mostra a senha temporária." />

      {/* Placar de adoção + filtros. Cada número do placar é clicável e vira o
          filtro correspondente — "3 nunca entraram" é a lista de cobrança. */}
      <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
        <Select allowClear showSearch placeholder="Todos os clientes" style={{ width: 280 }}
          value={fCliente ?? undefined} onChange={(v) => setFCliente(v ?? null)}
          filterOption={(i, o) => String(o?.label || '').toLowerCase().includes(i.toLowerCase())}
          options={clientes
            .filter(c => usuarios.some(u => u.cliente_id === c.id))
            .map(c => ({ value: c.id, label: c.nome }))} />
        <Select value={fSituacao} onChange={setFSituacao} style={{ width: 200 }}
          options={[
            { value: 'todos',   label: `Todos (${contagem.total})` },
            { value: 'nunca',   label: `Nunca entraram (${contagem.nunca})` },
            { value: 'online',  label: `Online agora (${contagem.online})` },
            { value: 'inativo', label: 'Desativados' },
          ]} />
        <Input.Search allowClear placeholder="Buscar nome ou e-mail" style={{ width: 240 }}
          value={fBusca} onChange={(e) => setFBusca(e.target.value)} />
        <Space size={6}>
          <Switch size="small" checked={agrupar} onChange={setAgrupar} />
          <Text type="secondary" style={{ fontSize: 12 }}>agrupar por cliente</Text>
        </Space>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
          {contagem.usou30} de {contagem.total} usaram o portal nos últimos 30 dias
        </Text>
      </Space>

      {agrupar ? (
        grupos.length === 0 ? (
          <Table rowKey="email" size="small" loading={loading} pagination={false}
            dataSource={[]} columns={columns} />
        ) : grupos.map((g, idx) => (
          <div key={g.cliente_id} style={{ marginBottom: 18 }}>
            <Space align="center" style={{ marginBottom: 6 }} size={8}>
              <Text strong style={{ fontSize: 13 }}>{g.nome}</Text>
              <Tag style={{ marginInlineEnd: 0 }}>{g.itens.length} usuário{g.itens.length > 1 ? 's' : ''}</Tag>
              {(() => {
                const nunca = g.itens.filter(u => !u.ultimo_acesso_em).length;
                const online = g.itens.filter(u => u.ultimo_acesso_em
                  && Date.now() - new Date(u.ultimo_acesso_em).getTime() < ONLINE_JANELA_MS).length;
                return <>
                  {online > 0 && <Tag color="green" style={{ marginInlineEnd: 0 }}>{online} online</Tag>}
                  {nunca > 0 && <Tag color="orange" style={{ marginInlineEnd: 0 }}>{nunca} nunca entrou</Tag>}
                </>;
              })()}
            </Space>
            <Table rowKey="email" size="small" pagination={false}
              loading={loading && idx === 0}
              showHeader={idx === 0}
              dataSource={g.itens} columns={columns}
              tableLayout="fixed" scroll={{ x: 900 }} />
          </div>
        ))
      ) : (
        <Table rowKey="email" size="small" loading={loading} pagination={false}
          dataSource={filtrados} columns={columns}
          tableLayout="fixed" scroll={{ x: 1000 }} />
      )}

      <Modal
        open={aberto}
        title={editando ? `Editar — ${editando.email}` : 'Novo usuário do portal'}
        onCancel={() => setAberto(false)}
        onOk={salvar}
        confirmLoading={salvando}
        okText="Salvar"
        okButtonProps={{ disabled: !podeGerenciar }}
        width={560}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text type="secondary">E-mail (login do cliente)</Text>
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
              disabled={!!editando} placeholder="pessoa@cliente.com.br" />
          </div>
          <div>
            <Text type="secondary">Nome</Text>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da pessoa" />
          </div>
          <div>
            <Text type="secondary">Cliente</Text>
            <Select showSearch style={{ width: '100%' }} value={clienteId ?? undefined}
              placeholder="Selecione o cliente"
              optionFilterProp="label"
              options={clientes.map((c) => ({ value: c.id, label: c.nome }))}
              onChange={(v) => { setClienteId(v); setContratosSel([]); carregarContratos(v); }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Text type="secondary">Papel</Text>
              <Select style={{ width: '100%' }} value={papel} onChange={(v) => setPapel(v)}
                options={[{ value: 'gestor', label: 'Gestor (vê tudo do cliente)' },
                          { value: 'comum', label: 'Comum (contratos marcados)' }]} />
            </div>
            <div>
              <Text type="secondary">Ativo</Text>
              <div><Switch checked={ativo} onChange={setAtivo} /></div>
            </div>
          </div>
          <div>
            <Text type="secondary">Contratos que enxerga {papel === 'gestor' && '(gestor vê todos — não precisa marcar)'}</Text>
            <Select mode="multiple" style={{ width: '100%' }} value={contratosSel}
              onChange={setContratosSel} disabled={papel === 'gestor' || !clienteId}
              placeholder={clienteId ? 'Selecione os contratos' : 'Escolha o cliente primeiro'}
              optionFilterProp="label"
              options={contratosOpts.map((c) => ({
                value: c.id,
                label: `${c.numero || c.id.slice(0, 8)}${c.titulo ? ' — ' + c.titulo : ''}`,
              }))} />
            {papel === 'comum' && contratosSel.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {contratosSel.map((id) => <Tag key={id}>{numeroPorId.get(id) || id.slice(0, 8)}</Tag>)}
              </div>
            )}
          </div>
        </Space>
      </Modal>
    </div>
  );
}
